import React from 'react';
import { useNavigate } from 'react-router-dom';
import './AtacamaOffersSection.css';
import { HandDrawnButton } from '../shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../shared/CTALink/CTALink';

// Import Figma assets for offers - correct paths
const offer1Image = "/assets/figma/227453f99d94bb3d016ef839d17a48958c8ba37d.png";
const offer2Image = "/assets/figma/f330f274e401d9c91d4aa67b3ec6d749e308c316.png";
const offer3Image = "/assets/figma/f0258d74f81dd9e2aea6126dbd1e9ca313304534.png";
const linkUnderline = "/assets/2467b76186c9ae07dfd7108710e0288f8891089e.svg";

interface OfferCard {
  image: string;
  title: string;
  linkText: string;
}

interface AtacamaOffersSectionProps {
  property?: string;
  title?: string;
  description?: string;
  offers?: OfferCard[];
}

const atacamaOffers: OfferCard[] = [
  {
    image: offer1Image,
    title: "Combine two or more of our lodges for the perfect South American Adventure",
    linkText: "VIEW MORE"
  },
  {
    image: offer2Image,
    title: "Combine two or more of our lodges for the perfect South American Adventure",
    linkText: "VIEW MORE"
  },
  {
    image: offer3Image,
    title: "Combine two or more of our lodges for the perfect South American Adventure",
    linkText: "VIEW MORE"
  }
];

const iguazuOffers: OfferCard[] = [
  {
    image: offer1Image,
    title: "Combine two or more of our lodges for the perfect South American Adventure.",
    linkText: "VIEW MORE"
  },
  {
    image: offer2Image,
    title: "Stay Longer. For a minimum of two nights booking, enjoy a complimentary night.",
    linkText: "VIEW MORE"
  },
  {
    image: offer3Image,
    title: "Awasi Atacama invites you to our next RE-ENERGIZE wellness immersion",
    linkText: "VIEW MORE"
  }
];

const mendozaOffers: OfferCard[] = [
  {
    image: offer1Image,
    title: "Combine two or more of our lodges for the perfect South American Adventure.",
    linkText: "VIEW MORE"
  },
  {
    image: offer2Image,
    title: "Stay Longer. For a minimum of two nights booking, enjoy a complimentary night.",
    linkText: "VIEW MORE"
  },
  {
    image: offer3Image,
    title: "Awasi Mendoza invites you to our next RE-ENERGIZE wellness immersion",
    linkText: "VIEW MORE"
  }
];

const santaCatarinaOffers: OfferCard[] = [
  {
    image: offer1Image,
    title: "Combine two or more of our lodges for the perfect South American Adventure.",
    linkText: "VIEW MORE"
  },
  {
    image: offer2Image,
    title: "Stay Longer. For a minimum of two nights booking, enjoy a complimentary night.",
    linkText: "VIEW MORE"
  },
  {
    image: offer3Image,
    title: "Awasi Santa Catarina invites you to our next RE-ENERGIZE wellness immersion",
    linkText: "VIEW MORE"
  }
];

/**
 * AtacamaOffersSection - Figma-exact implementation of offers section (584-8768)
 * Features:
 * - Large title positioned exactly as in Figma
 * - Description text with CTA button
 * - Three offer cards with exact positioning
 * - Exact typography and spacing from Figma
 */
export const AtacamaOffersSection: React.FC<AtacamaOffersSectionProps> = ({ 
  property = "atacama",
  title,
  description,
  offers: customOffers
}) => {
  const navigate = useNavigate();
  
  // Get property-specific content
  const getContentForProperty = () => {
    switch (property.toLowerCase()) {
      case 'iguazu':
        return {
          title: title || "Are you ready to explore the natural wonders of South America?",
          description: description || "Every journey at Awasi immerses guests in the native stories and traditions of each destination. We offer private Villa stays that ensure authenticity, comfort and connection to the surroundings. Choose one or a combination of our locations and enjoy stunning landscapes, native flavours and hidden gems.",
          offers: customOffers || iguazuOffers
        };
      case 'mendoza':
        return {
          title: title || "Are you ready to explore the natural wonders of South America?",
          description: description || "Every journey at Awasi immerses guests in the native stories and traditions of each destination. Choose one or a combination of our locations and enjoy stunning landscapes, native flavours and hidden gems.",
          offers: customOffers || mendozaOffers
        };
      case 'santa-catarina':
      case 'santacatarina':
        return {
          title: title || "Are you ready to explore the natural wonders of South America?",
          description: description || "Every journey at Awasi immerses guests in the native stories and traditions of each destination. We offer private Villa stays that ensure authenticity, comfort and connection to the surroundings. Choose one or a combination of our locations and enjoy stunning landscapes, native flavours and hidden gems.",
          offers: customOffers || santaCatarinaOffers
        };
      case 'atacama':
      default:
        return {
          title: title || "Create your South American journey",
          description: description || "Make sure to create your memorable journey combining visits to our five remote locations in South America. Connect, recharge, explore and enjoy immersive, personal and authentic experiences.",
          offers: customOffers || atacamaOffers
        };
    }
  };
  
  return (
    <div className="atacama-offers-section">
      <div className="atacama-offers-container">
        {/* Main Title - positioned exactly as in Figma */}
        <h2 className="atacama-offers-main-title">
          {getContentForProperty().title}
        </h2>
        
        {/* Description and CTA Button */}
        <div className="atacama-offers-intro">
          <p className="atacama-offers-description">
            {getContentForProperty().description}
          </p>
          <HandDrawnButton
            text="SEE OFFERS"
            variant={property as any}
            color="awasi"
            animation="stroke-to-fill"
            onClick={() => navigate('/offers')}
          />
        </div>

        {/* Offers Cards - positioned exactly as in Figma */}
        <div className="atacama-offers-cards">
          {getContentForProperty().offers.map((offer, index) => (
            <div key={index} className={`atacama-offer-card atacama-offer-card-${index + 1}`}>
              <div className="atacama-offer-image-container">
                <div 
                  className="atacama-offer-image"
                  style={{ backgroundImage: `url(${offer.image})` }}
                />
              </div>
              <div className="atacama-offer-content">
                <h3 className="atacama-offer-title">{offer.title}</h3>
                <CTALink
                  text={offer.linkText}
                  type="primary"
                  color="awasi"
                  href="/offers"
                  className="atacama-offer-link"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AtacamaOffersSection;